import type { KeyboardEvent as ReactKeyboardEvent } from "react"

import { isCustomContactEmail } from "@productlane/lib/src/custom-channel"

export const THREAD_MENU_COPY = {
	markDone: "Mark done",
	moveToInbox: "Move to Inbox",
	priority: "Priority",
	assignee: "Assignee",
	tags: "Tags",
	issueOrProject: "Issue or project",
	person: "Person",
	customer: "Customer",
	markRead: "Mark as read",
	markUnread: "Mark as unread",
	snooze: "Snooze",
	unsnooze: "Unsnooze",
	snoozeCustom: "Pick a date...",
	subscribe: "Subscribe",
	unsubscribe: "Unsubscribe",
	copy: "Copy",
	openInNewTab: "Open in new tab",
	openInSlack: "Open in Slack",
	delete: "Delete",
	search: {
		priority: "Set priority...",
		assignee: "Assign to...",
		tags: "Add tags...",
		issueOrProject: "Link issue or project...",
		person: "Search people...",
		customer: "Search customers...",
	},
	empty: {
		priority: "No matching priority",
		assignee: "No matching teammates",
		tags: "No matching tags",
		issueOrProject: "No matching issues or projects",
		person: "No matching people",
		customer: "No matching customers",
		linearTeams: "Pick your Linear teams in settings first",
	},
	unassign: "Unassign",
	noPerson: "No person",
	noCustomer: "No customer",
	deleteTitle: "Are you sure?",
	deleteDescription: "Do you really want to delete this thread?",
	failed: "Couldn't update this thread. Try again.",
	bulkFailed: "Couldn't update these threads. Try again.",
	bulkDeleteDescription: (count: number) =>
		`Do you really want to delete ${threadCount(count)}?`,
	copyFailed: "Couldn't copy to the clipboard",
}

/** Done and closed-loop threads go back to the inbox; every other state can be marked done. */
export function isDoneState(state: string): boolean {
	return state === "PROCESSED" || state === "COMPLETED"
}

/** The selection a right-click acts on: every selected thread when the clicked row is one of several. */
export function bulkMenuTargets(
	selectedIds: readonly string[],
	clickedId: string,
): string[] | null {
	if (selectedIds.length < 2 || !selectedIds.includes(clickedId)) return null
	return [...selectedIds]
}

interface BulkMenuThread {
	state: string
	isRead: boolean
	painLevel: string
	assigneeId?: string | null
}

/** What the bulk menu offers: a toggle flips only when every selected thread already has that state. */
export function bulkMenuState(threads: readonly BulkMenuThread[]) {
	const every = (check: (thread: BulkMenuThread) => boolean) =>
		threads.length > 0 && threads.every(check)
	const shared = <T>(pick: (thread: BulkMenuThread) => T): T | null => {
		const first = threads[0]
		if (!first) return null
		const value = pick(first)
		return threads.every((thread) => pick(thread) === value) ? value : null
	}
	return {
		allDone: every((thread) => isDoneState(thread.state)),
		allRead: every((thread) => thread.isRead),
		allSnoozed: every((thread) => thread.state === "SNOOZED"),
		painLevel: shared((thread) => thread.painLevel),
		assigneeId: shared((thread) => thread.assigneeId ?? null),
	}
}

export function threadCount(count: number): string {
	return `${count} thread${count === 1 ? "" : "s"}`
}

type ShortcutEvent = Pick<
	KeyboardEvent,
	"key" | "metaKey" | "ctrlKey" | "shiftKey" | "altKey"
> &
	Partial<Pick<KeyboardEvent, "code">>

const MODIFIER_KEYS = new Set(["meta", "control", "shift", "alt"])

/** A keystroke as the `data-shortcut` value a menu row declares: "e", "mod+backspace". */
export function shortcutCombo(event: ShortcutEvent): string | null {
	// macOS Option turns the letter into another glyph (Option+S types "ß").
	const letter = event.altKey && /^Key[A-Z]$/.exec(event.code ?? "")
	const key = letter
		? letter[0].slice(3).toLowerCase()
		: event.key.toLowerCase()
	if (!key || MODIFIER_KEYS.has(key)) return null
	const parts: string[] = []
	if (event.metaKey || event.ctrlKey) parts.push("mod")
	if (event.altKey) parts.push("alt")
	if (event.shiftKey) parts.push("shift")
	parts.push(key)
	return parts.join("+")
}

/** Rows only, never a search field: a typed letter belongs to the field. */
export function shortcutRow(
	content: HTMLElement,
	target: EventTarget | null,
	event: ShortcutEvent,
): HTMLElement | null {
	if (!(target instanceof HTMLElement)) return null
	if (target.closest("input, textarea, [contenteditable]")) return null
	// A submenu's keys are its own; the rows declaring shortcuts live one level up.
	if (target.closest("[data-radix-menu-content]") !== content) return null
	const combo = shortcutCombo(event)
	if (!combo) return null
	return (
		Array.from(content.querySelectorAll<HTMLElement>("[data-shortcut]")).find(
			(row) =>
				row.dataset.shortcut === combo && !row.hasAttribute("data-disabled"),
		) ?? null
	)
}

/** Clicks the row a keystroke names, the way Linear's menus act on their listed shortcuts. */
export function handleMenuShortcut(event: ReactKeyboardEvent<HTMLElement>) {
	const row = shortcutRow(event.currentTarget, event.target, event)
	if (!row) return
	event.preventDefault()
	row.click()
}

export interface CopyOption {
	key: "link" | "number" | "title" | "email" | "portal"
	label: string
	value: string
	copied: string
}

export interface CopyableThread {
	id: string
	number?: number | null
	title?: string | null
	contact?: { email?: string | null } | null
}

export function threadPath(threadId: string) {
	return `/threads/${threadId}`
}

/** What a thread row offers to copy, in menu order; empty values are left out. */
export function threadCopyOptions(
	thread: CopyableThread,
	{
		origin,
		link,
		portalBaseUrl,
	}: {
		origin: string
		/** Overrides the thread link, for a notification pointing somewhere else. */
		link?: string | null
		/** Set when the Support Portal is on. */
		portalBaseUrl?: string | null
	},
): CopyOption[] {
	const options: CopyOption[] = [
		{
			key: "link",
			label: "Copy link",
			value: link ?? `${origin}${threadPath(thread.id)}`,
			copied: "Link copied",
		},
	]
	if (thread.number != null) {
		options.push({
			key: "number",
			label: "Copy thread number",
			value: `#${thread.number}`,
			copied: "Copied thread number to clipboard",
		})
	}
	const title = thread.title?.trim()
	if (title) {
		options.push({
			key: "title",
			label: "Copy title",
			value: title,
			copied: "Title copied",
		})
	}
	const email = thread.contact?.email?.trim()
	if (email && !isCustomContactEmail(email)) {
		options.push({
			key: "email",
			label: "Copy customer email",
			value: email,
			copied: "Email copied",
		})
	}
	if (portalBaseUrl) {
		options.push({
			key: "portal",
			label: "Copy Support Portal link",
			value: `${portalBaseUrl}requests?r=${thread.id}`,
			copied: "Support portal link copied",
		})
	}
	return options
}
